import { useEffect, useMemo, useState } from "react";
import {
  ClipboardList,
  Heart,
  Wallet,
  Activity,
  Stethoscope,
  Scissors,
  Users2,
  FileText,
  Loader2,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import { Label } from "@project/components/ui/label";
import { Switch } from "@project/components/ui/switch";
import {
  FormStepper,
  type FormStep,
} from "@project/user/components/form-stepper";
import { formatDate } from "@project/lib/date-format";
import { getApiErrorMessage } from "@project/lib/api-error";
import { useI18n } from "@project/lib/use-i18n";
import { cn } from "@project/lib/utils";
import { admissionInquiryService } from "@project/services/admin/admission-inquiry.service";
import {
  formatInquiryNumber,
  normalizeInquiryStatus,
  type AdmissionInquiryRecord,
  type AdmissionInquiryStatus,
} from "@project/types/admission-inquiry";

type StepKey =
  | "inquiry"
  | "family"
  | "elder"
  | "income"
  | "health"
  | "treatment"
  | "operation"
  | "notes";

type StepConfig = FormStep & {
  icon: LucideIcon;
  eyebrow: string;
  subtitle: string;
};

const STEP_DEFS: { key: StepKey; icon: LucideIcon }[] = [
  { key: "inquiry", icon: ClipboardList },
  { key: "family", icon: Users2 },
  { key: "elder", icon: Heart },
  { key: "income", icon: Wallet },
  { key: "health", icon: Activity },
  { key: "treatment", icon: Stethoscope },
  { key: "operation", icon: Scissors },
  { key: "notes", icon: FileText },
];

function statusBadge(status: AdmissionInquiryStatus) {
  const normalized = normalizeInquiryStatus(status);
  const map = {
    pending: {
      label: "Pending",
      className: "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15",
    },
    approved: {
      label: "Approved",
      className: "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15",
    },
    rejected: {
      label: "Rejected",
      className: "bg-rose-500/15 text-rose-700 hover:bg-rose-500/15",
    },
  } as const;
  const item = map[normalized];
  return <Badge className={item.className}>{item.label}</Badge>;
}

function displayValue(value?: string | number | null) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return String(value);
}

function ViewField({
  label,
  value,
  full = false,
}: {
  label: string;
  value?: string | number | null;
  full?: boolean;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
        {label}
      </Label>
      <div className="bg-muted/40 whitespace-pre-wrap rounded-xl border border-transparent px-3 py-2.5 text-sm text-foreground">
        {displayValue(value)}
      </div>
    </div>
  );
}

function ToggleViewField({
  label,
  checked,
  className,
}: {
  label: string;
  checked: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-4 py-3 md:col-span-2",
        className
      )}
    >
      <p className="text-foreground/90 text-sm font-medium">{label}</p>
      <Switch checked={checked} disabled aria-readonly />
    </div>
  );
}

function StepPanel({
  step,
  children,
}: {
  step: StepConfig;
  children: React.ReactNode;
}) {
  const Icon = step.icon;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-3 border-b border-border pb-4">
        <div className="bg-primary/10 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
            {step.eyebrow}
          </div>
          <h3 className="font-serif text-lg font-bold text-foreground sm:text-xl">
            {step.label}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
            {step.subtitle}
          </p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="bg-muted/30 rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

export type AdmissionInquiryDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inquiryId: number | null;
  preview?: AdmissionInquiryRecord | null;
};

export function AdmissionInquiryDetailDialog({
  open,
  onOpenChange,
  inquiryId,
  preview,
}: AdmissionInquiryDetailDialogProps) {
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [record, setRecord] = useState<AdmissionInquiryRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps: StepConfig[] = useMemo(
    () =>
      STEP_DEFS.map(def => ({
        icon: def.icon,
        label: t(`account.inquiryForm.steps.${def.key}.label`),
        shortLabel: t(`account.inquiryForm.steps.${def.key}.shortLabel`),
        eyebrow: t(`account.inquiryForm.steps.${def.key}.eyebrow`),
        subtitle: t(`account.inquiryForm.steps.${def.key}.subtitle`),
      })),
    [t]
  );

  const current = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const fp = (key: string) => t(`account.inquiryForm.fields.${key}.label`);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setRecord(null);
      setError(null);
      return;
    }
    if (!inquiryId) {
      return;
    }

    let ignore = false;
    setLoading(true);
    setError(null);
    setRecord(preview ?? null);

    void admissionInquiryService
      .getInquiry(inquiryId)
      .then(data => {
        if (ignore) {
          return;
        }
        if (data) {
          setRecord(data);
        } else {
          setError("Inquiry details could not be loaded.");
        }
      })
      .catch(err => {
        if (!ignore) {
          setError(getApiErrorMessage(err, "Failed to load inquiry details."));
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [open, inquiryId, preview]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
          <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
            <div>
              <DialogTitle className="font-serif text-xl font-bold">
                {record ? formatInquiryNumber(record) : "Inquiry Details"}
              </DialogTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {record
                  ? `${record.elder_name} · submitted ${formatDate(record.created_at) || "—"}`
                  : "Read-only view of the admission inquiry"}
              </p>
            </div>
            {record && statusBadge(record.status)}
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading && !record ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mb-3 h-8 w-8 animate-spin" />
              Loading inquiry…
            </div>
          ) : error && !record ? (
            <div className="border-destructive/30 bg-destructive/5 rounded-xl border px-4 py-6 text-center text-sm text-destructive">
              {error}
            </div>
          ) : record ? (
            <>
              <FormStepper steps={steps} currentStep={step} className="mb-6" />

              {step === 0 && (
                <StepPanel step={current}>
                  <ViewField
                    label={fp("applicationDate")}
                    value={
                      record.application_date
                        ? formatDate(record.application_date)
                        : "—"
                    }
                  />
                  <ViewField
                    label={fp("inquirerName")}
                    value={record.inquirer_name}
                  />
                  <ViewField
                    label={fp("inquirerMobile")}
                    value={record.inquirer_mobile}
                  />
                  <ViewField
                    label={fp("inquirerEmail")}
                    value={record.inquirer_email}
                  />
                  <ViewField
                    label={fp("district")}
                    value={record.district_name}
                  />
                  <ViewField label={fp("city")} value={record.city_name} />
                  <ViewField label={fp("area")} value={record.area_name} />
                  <ViewField label={fp("pincode")} value={record.pincode} />
                  <ViewField
                    label={fp("landmark")}
                    value={record.landmark}
                    full
                  />
                  <ViewField
                    label={fp("referencePersonName")}
                    value={record.reference_person_name}
                  />
                  <ViewField
                    label={fp("referencePersonMobile")}
                    value={record.reference_person_mobile}
                  />
                </StepPanel>
              )}

              {step === 1 && (
                <StepPanel step={current}>
                  <div className="md:col-span-2">
                    <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
                      {fp("familyMembers")}
                    </Label>
                    {(record.family_members?.length ?? 0) > 0 ? (
                      <div className="space-y-3">
                        {record.family_members.map((member, i) => (
                          <div
                            key={member.id ?? i}
                            className="rounded-xl border border-border bg-background p-4"
                          >
                            <div className="mb-3 border-b border-border pb-2">
                              <span className="text-xs font-semibold text-muted-foreground">
                                {t(
                                  "account.inquiryForm.fields.familyMembers.member"
                                )}{" "}
                                {i + 1}
                              </span>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              <ViewField
                                label={t(
                                  "account.inquiryForm.fields.familyMembers.name"
                                )}
                                value={member.name}
                              />
                              <ViewField
                                label={t(
                                  "account.inquiryForm.fields.familyMembers.relationship"
                                )}
                                value={
                                  member.relationship_name
                                    ? member.relationship_code
                                      ? `${member.relationship_name} (${member.relationship_code})`
                                      : member.relationship_name
                                    : typeof member.relationship === "string"
                                      ? member.relationship || "—"
                                      : "—"
                                }
                              />
                              <ViewField
                                label={t(
                                  "account.inquiryForm.fields.familyMembers.mobile"
                                )}
                                value={member.mobile}
                              />
                              <ViewField
                                label={t(
                                  "account.inquiryForm.fields.familyMembers.details"
                                )}
                                value={member.details}
                                full
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState message="No family members added." />
                    )}
                  </div>
                </StepPanel>
              )}

              {step === 2 && (
                <StepPanel step={current}>
                  <ViewField
                    label={fp("elderName")}
                    value={record.elder_name}
                  />
                  <ViewField
                    label={fp("elderDistrict")}
                    value={record.elder_district_name}
                  />
                  <ViewField
                    label={fp("elderCity")}
                    value={record.elder_city_name}
                  />
                  <ViewField
                    label={fp("elderArea")}
                    value={record.elder_area_name}
                  />
                  <ViewField
                    label={fp("elderPincode")}
                    value={record.elder_pincode}
                  />
                  <ViewField
                    label={fp("elderLandmark")}
                    value={record.elder_landmark}
                    full
                  />
                  <ViewField
                    label={fp("physicalHealthStatus")}
                    value={record.physical_health_status}
                  />
                  <ViewField
                    label={fp("elderWeight")}
                    value={
                      record.elder_weight === null ||
                      record.elder_weight === undefined ||
                      record.elder_weight === ""
                        ? "—"
                        : `${record.elder_weight} kg`
                    }
                  />
                  <ViewField
                    label={fp("reasonForAdmission")}
                    value={record.reason_for_admission}
                    full
                  />
                </StepPanel>
              )}

              {step === 3 && (
                <StepPanel step={current}>
                  <ViewField
                    label={fp("pensionAmount")}
                    value={record.pension_amount}
                  />
                  <ViewField
                    label={fp("otherIncome")}
                    value={record.other_income}
                  />
                  <ViewField
                    label={fp("otherFinancierName")}
                    value={record.other_financier_name}
                  />
                  <ViewField
                    label={fp("otherFinancierNumber")}
                    value={record.other_financier_number}
                  />
                  <ViewField
                    label={fp("otherFinancierEmail")}
                    value={record.other_financier_email}
                    full
                  />
                </StepPanel>
              )}

              {step === 4 && (
                <StepPanel step={current}>
                  <ToggleViewField
                    label={fp("hasPhysicalDisability")}
                    checked={Boolean(record.has_physical_disability)}
                  />
                  {record.has_physical_disability && (
                    <ViewField
                      label={fp("physicalDisabilityDetails")}
                      value={record.physical_disability_details}
                      full
                    />
                  )}
                  <ToggleViewField
                    label={fp("hasMentalHealthIssue")}
                    checked={Boolean(record.has_mental_health_issue)}
                  />
                  {record.has_mental_health_issue && (
                    <ViewField
                      label={fp("mentalHealthIssueDetails")}
                      value={record.mental_health_issue_details}
                      full
                    />
                  )}
                  <ToggleViewField
                    label={fp("hasExistingDisease")}
                    checked={Boolean(record.has_existing_disease)}
                  />
                  {record.has_existing_disease && (
                    <ViewField
                      label={fp("diseaseDetails")}
                      value={record.disease_details}
                      full
                    />
                  )}
                  <ToggleViewField
                    label={fp("hasFoodAllergy")}
                    checked={Boolean(record.has_food_allergy)}
                  />
                  {record.has_food_allergy && (
                    <ViewField
                      label={fp("foodAllergyDetails")}
                      value={record.food_allergy_details}
                      full
                    />
                  )}
                  <ToggleViewField
                    label={fp("hasMedicineAllergy")}
                    checked={Boolean(record.has_medicine_allergy)}
                  />
                  {record.has_medicine_allergy && (
                    <ViewField
                      label={fp("medicineAllergyDetails")}
                      value={record.medicine_allergy_details}
                      full
                    />
                  )}
                  <ViewField
                    label={fp("specialMedicalInstructions")}
                    value={record.special_medical_instructions}
                    full
                  />
                </StepPanel>
              )}

              {step === 5 && (
                <StepPanel step={current}>
                  <ToggleViewField
                    label={fp("currentlyUnderTreatment")}
                    checked={Boolean(record.currently_under_treatment)}
                  />
                  <ViewField
                    label={fp("currentDoctorName")}
                    value={record.current_doctor_name}
                  />
                  <ViewField
                    label={fp("currentHospitalName")}
                    value={record.current_hospital_name}
                  />
                  <ViewField
                    label={fp("currentHospitalAddress")}
                    value={record.current_hospital_address}
                    full
                  />
                  <ToggleViewField
                    label={fp("takesCurrentMedicines")}
                    checked={Boolean(record.takes_current_medicines)}
                  />
                  {record.takes_current_medicines && (
                    <div className="md:col-span-2">
                      <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
                        {fp("medicines")}
                      </Label>
                      {(record.medicines?.length ?? 0) > 0 ? (
                        <div className="space-y-3">
                          {record.medicines.map((medicine, i) => (
                            <div
                              key={medicine.id ?? i}
                              className="rounded-xl border border-border bg-background p-4"
                            >
                              <div className="mb-3 border-b border-border pb-2">
                                <span className="text-xs font-semibold text-muted-foreground">
                                  {t(
                                    "account.inquiryForm.fields.medicines.medicine"
                                  )}{" "}
                                  {i + 1}
                                </span>
                              </div>
                              <div className="grid gap-3 md:grid-cols-2">
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.medicines.treatmentName"
                                  )}
                                  value={medicine.current_treatment_name}
                                  full
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.medicines.name"
                                  )}
                                  value={medicine.name}
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.medicines.dosage"
                                  )}
                                  value={medicine.dosage}
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.medicines.frequency"
                                  )}
                                  value={medicine.frequency}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState message="No medicines added." />
                      )}
                    </div>
                  )}
                </StepPanel>
              )}

              {step === 6 && (
                <StepPanel step={current}>
                  <ToggleViewField
                    label={fp("hadOperationBefore")}
                    checked={Boolean(record.had_operation_before)}
                  />
                  {record.had_operation_before && (
                    <div className="md:col-span-2">
                      <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
                        {fp("operations")}
                      </Label>
                      {(record.operations?.length ?? 0) > 0 ? (
                        <div className="space-y-3">
                          {record.operations.map((operation, i) => (
                            <div
                              key={operation.id ?? i}
                              className="rounded-xl border border-border bg-background p-4"
                            >
                              <div className="mb-3 border-b border-border pb-2">
                                <span className="text-xs font-semibold text-muted-foreground">
                                  {t(
                                    "account.inquiryForm.fields.operations.operation"
                                  )}{" "}
                                  {i + 1}
                                </span>
                              </div>
                              <div className="grid gap-3 md:grid-cols-2">
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.type"
                                  )}
                                  value={operation.operation_type}
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.date"
                                  )}
                                  value={
                                    operation.operation_date
                                      ? formatDate(operation.operation_date)
                                      : "—"
                                  }
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.doctorName"
                                  )}
                                  value={operation.operation_doctor_name}
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.hospitalName"
                                  )}
                                  value={operation.operation_hospital_name}
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.district.label"
                                  )}
                                  value={operation.district_name}
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.city.label"
                                  )}
                                  value={operation.city_name}
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.area.label"
                                  )}
                                  value={operation.area_name}
                                  full
                                />
                                <ViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.details"
                                  )}
                                  value={operation.operation_details}
                                  full
                                />
                                <ToggleViewField
                                  label={t(
                                    "account.inquiryForm.fields.operations.takesRecoveryMedicine"
                                  )}
                                  checked={Boolean(
                                    operation.takes_recovery_medicine
                                  )}
                                  className="bg-card"
                                />
                                {operation.takes_recovery_medicine && (
                                  <ViewField
                                    label={t(
                                      "account.inquiryForm.fields.operations.recoveryMedicineDetails"
                                    )}
                                    value={operation.recovery_medicine_details}
                                    full
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState message="No operations added." />
                      )}
                    </div>
                  )}
                </StepPanel>
              )}

              {step === 7 && (
                <StepPanel step={current}>
                  <ViewField
                    label={fp("additionalNotes")}
                    value={record.additional_notes}
                    full
                  />
                </StepPanel>
              )}
            </>
          ) : null}
        </div>

        {record && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(s => s - 1)}
              disabled={isFirst}
              className="gap-1 rounded-xl"
            >
              <ChevronLeft className="h-4 w-4" />{" "}
              {t("account.inquiryForm.nav.previous")}
            </Button>
            <p className="text-xs text-muted-foreground">
              Step {step + 1} of {steps.length}
            </p>
            {isLast ? (
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-xl"
              >
                Close
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setStep(s => s + 1)}
                className="gap-1 rounded-xl"
              >
                {t("account.inquiryForm.nav.next")}{" "}
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
